import { TransportJobsApiService } from 'core/api';
import { ITransportJobDao, ITransportProof } from 'core/api/types/transport-job.interface';
import { TransportJobStatus } from 'core/constants/transport-job-status';
import { useDialog } from 'core/providers/DialogProvider';
import dayjs from 'dayjs';
import ViewLocationDialog from 'modules/run-activity/dialogs/view-location-dialog';
import { useEffect, useState } from 'react';
import SharedButton from 'shared/components/buttons/button';
import SkeletonElement from 'shared/components/layout/skeleton-element';

interface ITransportJobOverviewProof {
  job: ITransportJobDao;
}

/**
 * Both halves of a job, as the driver recorded them.
 *
 * This used to show one thing — the signature at the end — which cannot answer
 * the question the office is actually asked on the phone: "has it been picked
 * up, and where was he?". Collection and delivery are now separate blocks, each
 * with the time, the driver, and the position captured at the moment they
 * tapped.
 *
 * Every field is optional on purpose. Jobs worked before proof existed carry
 * neither position, a job can be delivered with no collection recorded against
 * it, and a driver who refuses location permission still completes the job. The
 * screen says which of those it is rather than leaving a blank.
 */
const TransportJobOverviewProof = ({ job }: ITransportJobOverviewProof) => {
  const dialog = useDialog();
  const [image, setImage] = useState<string>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const prepare = async () => {
      const proofUrl = await TransportJobsApiService.getSignature(job.uid);
      setImage(proofUrl);
      setLoading(false);
    };
    if (job.status === TransportJobStatus.COMPLETED) {
      prepare();
    } else {
      setLoading(false);
    }
  }, [job.status, job.uid]);

  const proofBlock = (title: string, proof: ITransportProof | undefined, missing: string) => (
    <div>
      <p className='headline-06'>{title}</p>
      {!proof ? (
        <p className='body-01 text-stone-300 mt-2'>{missing}</p>
      ) : (
        <div className='mt-2'>
          <p className='body-02 text-gray-500'>When</p>
          <p className='body-01'>{dayjs(proof.at.toDate()).format('HH:mm, DD/MM/YYYY')}</p>
          <p className='body-02 text-gray-500 mt-3'>By</p>
          <p className='body-01'>{proof.by?.fullName ?? 'Unknown'}</p>
          <div className='mt-3'>
            {proof.location ? (
              <SharedButton
                onClick={() => dialog?.openDialog(<ViewLocationDialog position={proof.location} />)}
                type='button'
                appearance='primary'
                label='View location'
              />
            ) : (
              // Said plainly rather than left out: "no location" is a fact about
              // the job, and a missing row reads as a screen that has not loaded.
              <p className='body-02 text-gray-500'>No location recorded</p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className='bg-white rounded-md shadow-md'>
      <div className='flex justify-between items-center p-4 border-b'>
        <p className='headline-06'>Proof of collection and delivery</p>
      </div>
      <div className='p-4'>
        {loading && <SkeletonElement width='340px' height='500px' />}
        {!loading && (
          <div className='space-y-6'>
            {proofBlock('Collected', job.collected, 'Not picked up yet')}

            {job.status === TransportJobStatus.UNFULFILLED ? (
              <div>
                <p className='headline-06'>Delivered</p>
                <p className='body-01 mt-2'>The driver was unable to complete this job</p>
                <p className='body-01'>Reason: {job.driverNote}</p>
              </div>
            ) : (
              proofBlock('Delivered', job.completed, 'Not delivered yet')
            )}

            {job.status === TransportJobStatus.COMPLETED && (
              <div>
                <p className='body-02 text-gray-500'>Signature</p>
                <img className='max-h-[500px] mt-2' src={image} alt='Signature taken on delivery' />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransportJobOverviewProof;
